import { useState, useEffect, useMemo } from 'react';
import { useAssignL2VPNToTerminal } from '@/domains/terminals/services/account-user-terminals';
import { useGetL2VPNSuggestions } from '@/domains/l2vpns/services/account-l2vpn-circuits';
import { AddL2VPNToTerminalDetails, CircuitTaggingType, L2VPNCircuit, MAX_VLAN, MIN_VLAN } from '@/shared/types/l2vpnTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Loader2, Plus, Trash2, AlertCircle, Info, Lightbulb } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { UserTerminal } from '@/shared/types/userTerminalTypes';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export interface CircuitMapping {
  id: string;
  taggingType: CircuitTaggingType;
  circuitId: string;
  circuitName: string;
  customerVlanStr: string; // Comma-separated, e.g., "200, 800"
  serviceVlan: number | 0; // Single VLAN ID, e.g. 200
}

interface AddL2VPNCircuitToTerminalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  terminal: UserTerminal;
  availableCircuits: L2VPNCircuit[];
}

export default function AddL2VPNCircuitToTerminalDialog({
  open,
  onOpenChange,
  onSuccess,
  terminal,
  availableCircuits,
}: AddL2VPNCircuitToTerminalDialogProps) {
  const { hasPermission } = usePermissions();
  const canManageTerminals = hasPermission(OrgType.TENANT, Feature.TERMINALS, Permission.MANAGE);

  const circuitIds = useMemo(
    () => availableCircuits?.map((circuit) => circuit.circuitId) ?? [],
    [availableCircuits],
  );

  const { data: l2vpnSuggestions } = useGetL2VPNSuggestions(circuitIds, open);

  const [taggingMethod, setTaggingMethod] = useState<CircuitTaggingType>(CircuitTaggingType.QINQ);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Internal storage for mappings of both tagging types
  const [mappingsStorage, setMappingsStorage] = useState<Map<CircuitTaggingType, CircuitMapping[]>>(new Map());

  // Assign L2VPN to terminal
  const assignL2VPNToTerminal = useAssignL2VPNToTerminal();

  // Helper function to get mappings for a specific tagging type
  const getMappingsForTaggingType = (taggingType: CircuitTaggingType): CircuitMapping[] => {
    return mappingsStorage.get(taggingType) || [];
  };

  // Helper function to update mappings for a specific tagging type
  const updateMappingsForTaggingType = (taggingType: CircuitTaggingType, newMappings: CircuitMapping[]) => {
    setMappingsStorage(prev => {
      const updated = new Map(prev);
      if (newMappings.length > 0) {
        updated.set(taggingType, newMappings);
      } else {
        updated.delete(taggingType);
      }
      return updated;
    });
  };

  const suggestionsForTaggingType = useMemo(
    () => (l2vpnSuggestions ?? []).filter((suggestion) => suggestion.taggingType === taggingMethod),
    [l2vpnSuggestions, taggingMethod],
  );

  const getCircuitDisplayName = (circuitId: string): string => {
    const circuit = availableCircuits?.find((item) => item.circuitId === circuitId);
    const names = circuit?.records
      ?.map((record) => record.circuitName)
      .filter((name): name is string => Boolean(name));
    return names?.length ? names.join(', ') : circuitId;
  };

  // Populate mappings from terminal's existing L2VPN circuits when dialog opens
  useEffect(() => {
    if (open) {
      setValidationErrors([]);

      // Check if terminal has existing L2VPN circuits
      if (terminal?.l2VpnCircuits && terminal.l2VpnCircuits.length > 0) {
        // Group circuits by tagging type
        const groupedByTaggingType = new Map<CircuitTaggingType, CircuitMapping[]>();

        terminal.l2VpnCircuits.forEach((circuit, index) => {
          const mapping: CircuitMapping = {
            id: `mapping-${Date.now()}-${index}`,
            taggingType: circuit.taggingType,
            circuitId: circuit.circuitId,
            circuitName: circuit.circuitName ?? '',
            customerVlanStr: circuit.customerVlans?.length > 0 ? circuit.customerVlans.join(',') : '',
            serviceVlan: circuit.serviceVlan,
          };

          const existing = groupedByTaggingType.get(circuit.taggingType) || [];
          groupedByTaggingType.set(circuit.taggingType, [...existing, mapping]);
        });

        // Store mappings for all tagging types
        setMappingsStorage(groupedByTaggingType);

        // Determine initial tagging method from existing circuits
        // If all circuits have the same tagging type, use that; otherwise default to QINQ
        const firstTaggingType = Array.from(groupedByTaggingType.keys())[0];
        const allSameTaggingType = Array.from(groupedByTaggingType.values()).every(
          mappings => mappings.every(m => m.taggingType === firstTaggingType)
        );

        const initialTaggingType = allSameTaggingType && firstTaggingType
          ? firstTaggingType
          : CircuitTaggingType.QINQ;

        setTaggingMethod(initialTaggingType);
      } else {
        // No existing circuits, reset to defaults
        setMappingsStorage(new Map());
        setTaggingMethod(CircuitTaggingType.QINQ);
      }
    }
  }, [open, terminal]);

  // Parse VLAN range string (e.g., "400-410") to array of numbers
  const parseVlanRange = (vlanString: string): number[] => {
    if (!vlanString.trim()) return [];

    const parts = vlanString.split(',').map(part => part.trim());
    const vlans: number[] = [];

    for (const part of parts) {
      if (part.includes('-')) {
        // Range format: "400-410"
        const [start, end] = part.split('-').map(s => Number.parseInt(s.trim()));
        if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            vlans.push(i);
          }
        }
      } else {
        // Single VLAN
        const vlan = Number.parseInt(part);
        if (!Number.isNaN(vlan)) {
          vlans.push(vlan);
        }
      }
    }
    return vlans;
  };

  // Parse circuit's VLANs (array of strings, possibly ranges) to array of numbers
  const parseVlans = (vlans: string[] | null): number[] => {
    if (!vlans || vlans.length === 0) return [];

    const allVlans: number[] = [];
    for (const vlanStr of vlans) {
      const parsed = parseVlanRange(vlanStr);
      allVlans.push(...parsed);
    }
    return allVlans;
  };

  // Check if a service VLAN is within the circuit's allocated range
  const isServiceVlanInCircuitRange = (circuitId: string, serviceVlan: number): boolean => {
    const circuit = availableCircuits?.find(c => c.circuitId === circuitId);
    if (!circuit || !circuit.records || circuit.records.length === 0) return false;

    // Parse all service VLAN ranges for this circuit
    const allocatedServiceVlanRange = parseVlans(circuit.records.flatMap(record => record.allocatedServiceVlanRange || []));
    // Check if the serviceVlan number is in the parsed VLANs
    if (Number.isNaN(serviceVlan) || serviceVlan < MIN_VLAN || serviceVlan > MAX_VLAN) return false;

    return allocatedServiceVlanRange.some(vlan => vlan === serviceVlan);
  };

  // Check if a customer VLAN is within the circuit's allocated range
  const isCustomerVlanInCircuitRange = (circuitId: string, customerVlan: number): boolean => {
    const circuit = availableCircuits?.find(c => c.circuitId === circuitId);
    if (!circuit || !circuit.records || circuit.records.length === 0) return false;

    // Parse all customer VLAN ranges for this circuit
    const allocatedCustomerVlanRange = parseVlans(circuit.records.flatMap(record => record.allocatedCustomerVlanRange || []));
    // Check if the customerVlan number is in the parsed VLANs
    if (Number.isNaN(customerVlan) || customerVlan < MIN_VLAN || customerVlan > MAX_VLAN) return false;

    return allocatedCustomerVlanRange.some(vlan => vlan === customerVlan);
  };

  // Validate customer VLANs based on tagging method
  const validateVlansForTaggingType = (taggingType: CircuitTaggingType): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate all mappings across all tagging types
    const mappings = getMappingsForTaggingType(taggingType);

    // Common validation for all mappings
    for (const mapping of mappings) {
      if (!mapping.circuitId) {
        errors.push('Please select a circuit for all mappings');
        continue;
      }

      // Rule 1: Validation for all customer VLANs must be between MIN_VLAN and MAX_VLAN
      if (!mapping.customerVlanStr || mapping.customerVlanStr.split(',').some(vlan => Number.isNaN(Number.parseInt(vlan.trim())) || Number.parseInt(vlan.trim()) < MIN_VLAN || Number.parseInt(vlan.trim()) > MAX_VLAN)) {
        errors.push(`Please enter customer VLANs for all mappings and it must be between ${MIN_VLAN} and ${MAX_VLAN}`);
        continue;
      }
    }

    switch (taggingType) {
      case CircuitTaggingType.QINQ:
        // Validate QINQ mappings
        const terminalCustomerVLANs: number[] = [];
        const terminalServiceVLANs: number[] = [];

        for (const mapping of mappings) {
          const customerVlans = mapping.customerVlanStr.split(',').map(vlan => Number.parseInt(vlan.trim()));
          // Rule 2: C-VLAN must be unique across all QINQ mappings
          if (terminalCustomerVLANs.some(vlan => customerVlans.includes(vlan))) {
            errors.push(`Customer VLANs ${customerVlans.join(', ')} is repeated across mappings. C-VLANs must be unique for Q-in-Q tagging.`);
          }
          terminalCustomerVLANs.push(...customerVlans);

          // Rule 3: S-VLAN validation for Q-in-Q
          if (!mapping.serviceVlan || mapping.serviceVlan < MIN_VLAN || mapping.serviceVlan > MAX_VLAN) {
            errors.push(`Please enter service VLAN for all mappings and it must be between ${MIN_VLAN} and ${MAX_VLAN}`);
            continue;
          }

          // Check if S-VLAN is within the circuit's allocated range
          if (!isServiceVlanInCircuitRange(mapping.circuitId, mapping.serviceVlan)) {
            errors.push(`Service VLAN ${mapping.serviceVlan} is not in the allocated range for circuit ${mapping.circuitId}.`);
            continue;
          }

          // Check if S-VLAN is unique across all QINQ mappings
          if (terminalServiceVLANs.includes(mapping.serviceVlan)) {
            errors.push(`Service VLAN ${mapping.serviceVlan} is repeated across mappings. S-VLANs must be unique for Q-in-Q tagging.`);
          }
          terminalServiceVLANs.push(mapping.serviceVlan);
        }
        break;
      case CircuitTaggingType.SINGLE:
        // Validate SINGLE mappings
        const singleCircuitCustomerVLANs: Map<string, number[]> = new Map();

        for (const mapping of mappings) {
          // Rule 2: Check if C-VLAN is within the circuit's allocated range
          const customerVlans = parseVlans(mapping.customerVlanStr.split(','));
          if (!customerVlans.every(vlan => isCustomerVlanInCircuitRange(mapping.circuitId, vlan))) {
            errors.push(`Customer VLANs ${customerVlans.join(', ')} is not in the allocated range for circuit ${mapping.circuitId}.`);
            continue;
          }

          // Rule 3: C-VLAN must be unique for the circuit
          const existingVLANs = singleCircuitCustomerVLANs.get(mapping.circuitId) || [];
          if (existingVLANs.some(vlan => customerVlans.includes(vlan))) {
            errors.push(`Customer VLANs ${customerVlans.join(', ')} is repeated across mappings. C-VLANs must be unique for Single tagging.`);
          }
          singleCircuitCustomerVLANs.set(mapping.circuitId, [...existingVLANs, ...customerVlans]);
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return { isValid: errors.length === 0, errors };
  };

  const handleAddMapping = () => {
    const newMapping: CircuitMapping = {
      id: `mapping-${Date.now()}`,
      taggingType: taggingMethod,
      circuitId: '',
      circuitName: '',
      customerVlanStr: '',
      serviceVlan: 0, // Single VLAN ID, e.g. 200
    };
    const mappings = getMappingsForTaggingType(taggingMethod);
    const updatedMappings = [...mappings, newMapping];
    updateMappingsForTaggingType(taggingMethod, updatedMappings);
    setValidationErrors([]);
  };

  const handleRemoveMapping = (id: string) => {
    const mappings = getMappingsForTaggingType(taggingMethod);
    const filtered = mappings.filter(m => m.id !== id);
    updateMappingsForTaggingType(taggingMethod, filtered);
    setValidationErrors([]);
  };

  const handleCircuitChange = (mappingId: string, circuitId: string) => {
    const mappings = getMappingsForTaggingType(taggingMethod);
    const updated = mappings.map(m => {
      if (m.id === mappingId) {
        return {
          ...m,
          circuitId,
          circuitName: getCircuitDisplayName(circuitId),
        };
      }
      return m;
    });
    updateMappingsForTaggingType(taggingMethod, updated);
    setValidationErrors([]);
  };

  const handleVlanInputChange = (mappingId: string, field: 'customerVlan' | 'serviceVlan', value: string) => {
    const mappings = getMappingsForTaggingType(taggingMethod);
    const updated = mappings.map(m => {
      if (m.id !== mappingId) return m;
      if (field === 'serviceVlan') {
        const filtered = value.replace(/[^0-9]/g, '');
        return { ...m, serviceVlan: filtered ? Number.parseInt(filtered, 10) : 0 };
      }
      // Customer VLAN: comma-separated integers; keep as string for the controlled input
      const filtered = value.replace(/[^0-9,\s]/g, '');
      return { ...m, customerVlanStr: filtered };
    });
    updateMappingsForTaggingType(taggingMethod, updated);
    setValidationErrors([]);
  };

  const handleSubmit = async () => {
    const validation = validateVlansForTaggingType(taggingMethod);
    if (!validation.isValid) {
      showError(validation.errors[0] || 'Validation failed');
      return;
    }

    try {
      // Convert all mappings (from all tagging types) to API format
      const mappings = getMappingsForTaggingType(taggingMethod);
      if (mappings.length === 0 && terminal.l2VpnCircuits?.length === 0) {
        showError(ERROR.L2VPN.NO_MAPPINGS);
        return;
      }

      const circuits: AddL2VPNToTerminalDetails[] = mappings.map(mapping => {
        return {
          taggingType: mapping.taggingType,
          circuitId: mapping.circuitId,
          circuitName: mapping.circuitName,
          customerVlans: parseVlans(mapping.customerVlanStr.split(',')),
          serviceVlan: mapping.serviceVlan || 0,
        };
      });

      await assignL2VPNToTerminal.mutateAsync({
        terminalId: terminal.userTerminalId,
        circuits,
      });

      showSuccess(SUCCESS.L2VPN.ASSIGNED_TO_TERMINAL);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.L2VPN.ASSIGN_TO_TERMINAL);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setMappingsStorage(new Map());
      setTaggingMethod(CircuitTaggingType.QINQ);
      setValidationErrors([]);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add L2VPN Circuit to Terminal</DialogTitle>
        </DialogHeader>

        {/* Tagging Method Component */}
        <Tabs
          className="flex-1 flex flex-col overflow-hidden"
          value={taggingMethod}
          onValueChange={(value) => setTaggingMethod(value as CircuitTaggingType)}
        >
          <TabsList className="grid w-full max-w-md grid-cols-2 shrink-0 w-full justify-center mx-auto mb-4">
            <TabsTrigger value={CircuitTaggingType.QINQ} className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
              <Info className="h-4 w-4" />
              Q-in-Q Tagging
            </TabsTrigger>
            <TabsTrigger value={CircuitTaggingType.SINGLE} className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
              <Info className="h-4 w-4" />
              Single Tagging
            </TabsTrigger>
          </TabsList>

          {/* Mapping Component */}
          {mappingComponentView(getMappingsForTaggingType(taggingMethod), availableCircuits, taggingMethod)}
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={() => handleDialogClose(false)}
            disabled={assignL2VPNToTerminal.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              assignL2VPNToTerminal.isPending ||
              validationErrors.length > 0 ||
              ((getMappingsForTaggingType(taggingMethod).length ?? 0) === 0 && terminal.l2VpnCircuits?.length === 0) ||
              !canManageTerminals
            }
          >
            {assignL2VPNToTerminal.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  function mappingComponentView(mappings: CircuitMapping[], circuits: L2VPNCircuit[], taggingMethod: CircuitTaggingType) {
    return (
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        <TabsContent value={taggingMethod} className="mt-0 space-y-4">

          {/* Available range suggestions for the selected tagging type */}
          {suggestionsForTaggingType.length > 0 && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">
                  Available ranges for {taggingMethod === CircuitTaggingType.QINQ ? 'Q-in-Q' : 'Single'} tagging
                </p>
              </div>
              <div className="space-y-2">
                {suggestionsForTaggingType.map((suggestion) => (
                  <div
                    key={`${suggestion.circuitId}-${suggestion.taggingType}`}
                    className="flex flex-row items-center justify-between gap-3 border rounded-lg p-3"
                  >
                    <div className="flex flex-row flex-1 items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{getCircuitDisplayName(suggestion.circuitId)}</p>
                      <div className="flex flex-row items-center gap-2">
                        <Badge variant="outline" className="font-mono">
                          Customer VLANs: {suggestion.customerVlanRange || '—'}
                        </Badge>
                        {taggingMethod === CircuitTaggingType.QINQ && (
                          <Badge variant="outline" className="font-mono">
                            Service VLANs: {suggestion.serviceVlanRange || '—'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tagging Instructions */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <div className="space-y-2">
                <p className="font-semibold">{taggingMethod === CircuitTaggingType.SINGLE ? 'Single Tagging Rules:' : 'Q-in-Q Tagging Rules:'}</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {taggingMethod === CircuitTaggingType.SINGLE ? (
                    <>
                      <li>Customer VLANs must be within allocated ranges.</li>
                      <li>Customer VLANs for a circuit must be unique.</li>
                      <li>Customer VLANs must be unique in the VLANs belonging to the same terminal.</li>
                    </>
                  ) : (
                    <>
                      <li>C-VLAN must be unique in the VLANs belonging to the same terminal.</li>
                      <li>VLANs can use any C-VLAN between 1 to 4,094 (it can be repeated in other service lines).</li>
                      <li>Each Service VLAN mapping has a maximum of one VLAN ID, per Starlink policy.</li>
                      <li>Service VLAN is required, and must be unique in the VLANs belonging to the same terminal.</li>
                      <li>Terminal can only use allocated S-VLAN ranges, and must be unique within same circuit.</li>
                    </>
                  )}
                </ul>
              </div>
            </AlertDescription>
          </Alert>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Circuit Mappings</h4>
              {canManageTerminals && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMapping}
                  disabled={!circuits || circuits.length === 0 || assignL2VPNToTerminal.isPending || mappings.length >= circuits.length}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Mapping
                </Button>
              )}
            </div>
            {mappings.length === 0 ? (
              <div className="text-center py-8 border rounded-lg">
                <p>No mappings added. Click "Add Mapping" to create one.</p>
              </div>
            ) : (
              <div className={`space-y-3 ${!canManageTerminals ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {mappings.map((mapping) => {
                  // Check against all mappings across all tagging types to prevent duplicate circuit selection
                  const mappings = getMappingsForTaggingType(taggingMethod);
                  const availableCircuitsForMapping = circuits.filter(circuit => {
                    return !mappings.some(m => m.id !== mapping.id && m.circuitId === circuit.circuitId);
                  });

                  return (
                    <div key={mapping.id} className="flex flex-row items-center justify-between gap-4 p-4 border rounded-lg bg-card w-full">
                      <div className={`grid ${taggingMethod === CircuitTaggingType.QINQ ? 'grid-cols-3' : 'grid-cols-2'} gap-4 w-full flex-1`}>
                        {/* Circuit Select */}
                        <div className="space-y-2">
                          <Label htmlFor={`circuit-${mapping.id}`} className="text-xs font-medium">
                            Circuit *
                          </Label>
                          <Select
                            value={mapping.circuitId}
                            onValueChange={(value) => handleCircuitChange(mapping.id, value)}
                          >
                            <SelectTrigger id={`circuit-${mapping.id}`}>
                              <SelectValue placeholder="Select circuit" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCircuitsForMapping.length === 0 ? (
                                <div className="px-2 py-1.5 text-sm">
                                  No circuits available
                                </div>
                              ) : (
                                availableCircuitsForMapping.map((circuit) => (
                                  <SelectItem key={circuit.circuitId} value={circuit.circuitId}>
                                    {circuit.records.map(record => record.circuitName).join(', ')}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`customerVlan-${mapping.id}`} className="text-xs font-medium">
                            Customer VLANs *
                          </Label>
                          <Input
                            id={`customerVlan-${mapping.id}`}
                            value={mapping.customerVlanStr === null || mapping.customerVlanStr === '' ? '' : mapping.customerVlanStr}
                            onChange={(e) => handleVlanInputChange(mapping.id, 'customerVlan', e.target.value)}
                            placeholder="e.g. 200, 400, 800"
                            type="text"
                            inputMode="text"
                            className="font-mono"
                          />
                        </div>

                        {taggingMethod === CircuitTaggingType.QINQ && (
                          <div className="space-y-2">
                            <Label htmlFor={`serviceVlan-${mapping.id}`} className="text-xs font-medium">
                              Service VLAN *
                            </Label>
                            <Input
                              id={`serviceVlan-${mapping.id}`}
                              value={mapping.serviceVlan === null || mapping.serviceVlan === 0 ? '' : mapping.serviceVlan.toString()}
                              onChange={(e) => handleVlanInputChange(mapping.id, 'serviceVlan', e.target.value)}
                              placeholder="e.g. 200"
                              type="text"
                              inputMode="numeric"
                              className="font-mono"
                            />
                          </div>
                        )}
                      </div>
                      {/* Remove Button */}
                      {handleRemoveMapping && canManageTerminals && (
                        <div className="flex items-start justify-end">
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            onClick={() => handleRemoveMapping(mapping.id)}
                            className="h-10 w-10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </div>
    );
  }
}
